import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { LinkContext } from "../../LinkProvider";
import AnimatedCTA from "../AnimatedCTA";
import bannerBg from "../../img/experience/banner-bg.webp";
import bannerGrid from "../../img/experience/banner-grid.svg";
import bannerHero from "../../img/experience/banner-hero.webp";
import bannerHero2 from "../../img/experience/banner-hero2.webp";

const REVEAL_COLS = 5;
const REVEAL_ROWS = 4;

const REVEAL_TILES = Array.from({ length: REVEAL_COLS * REVEAL_ROWS }, (_, index) => {
  const col = index % REVEAL_COLS;
  const row = Math.floor(index / REVEAL_COLS);
  const order = (index * 37) % (REVEAL_COLS * REVEAL_ROWS);
  const horizontal = (index + row) % 2 === 0;
  return {
    id: index,
    className: horizontal ? "banner-reveal-x" : "banner-reveal-y",
    style: {
      left: `${(col / REVEAL_COLS) * 100}%`,
      top: `${(row / REVEAL_ROWS) * 100}%`,
      width: `${100 / REVEAL_COLS}%`,
      height: `${100 / REVEAL_ROWS}%`,
      animationDelay: `${80 + order * 60}ms`,
      transformOrigin: horizontal
        ? ((index + row) % 2 === 0 ? "left" : "right")
        : ((index + col) % 2 === 0 ? "top" : "bottom"),
    },
  };
});

function Banner() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);

  return (
    <section className="relative bg-[#020202] overflow-hidden min-h-[800px] xl:h-[955px]">

      {/* ── Desktop: 1512px centered visual container (Figma exact) ── */}
      <div className="hidden xl:block absolute left-1/2 -translate-x-1/2 top-0 w-[1512px] h-[955px] overflow-clip pointer-events-none">

        {/* Background dark image */}
        <div className="absolute left-0 top-[6px] w-[1512px] h-[949px] overflow-hidden">
          <img
            src={bannerBg}
            alt=""
            aria-hidden
            className="absolute left-0 max-w-none w-full"
            style={{ height: "119.49%", top: "-0.05%" }}
          />
        </div>

        {/* Grid overlay */}
        <div className="absolute inset-0 w-[1512px] h-[955px]">
          <img src={bannerGrid} alt="" aria-hidden className="absolute block inset-0 max-w-none w-full h-full" />
        </div>

        {/* Secondary glow / reflection */}
        <div
          className="absolute overflow-hidden"
          style={{ left: "720.23px", top: "717.91px", width: "590.947px", height: "237.086px" }}
        >
          <img
            src={bannerHero2}
            alt=""
            aria-hidden
            className="absolute max-w-none"
            style={{ width: "150.18%", height: "374.33%", left: "-23.35%", top: "-274.33%" }}
          />
        </div>

        {/* Main hero image (vault + bitcoin) */}
        <div
          className="absolute overflow-hidden"
          style={{ left: "699px", top: "185px", width: "622.794px", height: "770px" }}
        >
          <img
            src={bannerHero}
            alt=""
            aria-hidden
            className="absolute max-w-none"
            style={{ width: "141.52%", height: "114.52%", left: "-18.34%", top: "-14.52%" }}
          />
        </div>
      </div>

      {/* ── Mobile background image ── */}
      <div className="xl:hidden absolute inset-0 overflow-hidden">
        <img
          src={bannerBg}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none opacity-70"
        />
        <img
          src={bannerGrid}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full pointer-events-none"
        />
      </div>

      {/* ── Mobile hero images (positioned in lower half of section) ── */}
      <div className="xl:hidden absolute pointer-events-none" style={{ left: "66px", top: "542px", width: "258px", height: "319px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={bannerHero}
            alt=""
            aria-hidden
            className="absolute max-w-none"
            style={{ width: "141.52%", height: "114.52%", left: "-18.34%", top: "-14.52%" }}
          />
        </div>
      </div>
      <div className="xl:hidden absolute pointer-events-none" style={{ left: "74.8px", top: "762.78px", width: "244.82px", height: "98.22px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={bannerHero2}
            alt=""
            aria-hidden
            className="absolute max-w-none"
            style={{ width: "150.18%", height: "374.33%", left: "-23.35%", top: "-274.33%" }}
          />
        </div>
      </div>

      {/* Left-fade gradient (desktop only) */}
      <div
        className="hidden xl:block absolute inset-0 pointer-events-none z-[2]"
        style={{
          background:
            "linear-gradient(to right, #020202 0%, #020202 35%, rgba(2,2,2,0.75) 52%, rgba(2,2,2,0) 72%)",
        }}
      />

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[120px] pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to top, #020202 0%, rgba(2,2,2,0) 100%)" }}
      />

      {/* Animated tiles (desktop only) */}
      <div className="hidden xl:block absolute top-[106px] left-1/2 -translate-x-1/2 z-[3] w-full max-w-[1220px] h-[744px] pointer-events-none overflow-hidden">
        {REVEAL_TILES.map((tile) => (
          <span key={tile.id} className={`banner-reveal-tile ${tile.className}`} style={tile.style} />
        ))}
      </div>

      {/* ── Text content ── */}
      <div className="relative z-[4] glcontainer">

        {/* Desktop */}
        <div className="hidden xl:flex flex-col gap-[211px] pt-[145px] w-[703px]">
          <div className="flex flex-col gap-[20px]">
            <p className="font-['Inter'] font-medium text-[14px] text-[#636363] tracking-[1.4px] uppercase leading-[1.2]">
              {t("exp.banner.subtitle")}
            </p>
            <h1 className="font-['Montserrat'] font-black text-[64px] text-[#e1e1e1] tracking-[-1.28px] leading-[1.2]">
              {t("exp.banner.title")}
            </h1>
          </div>
          <div className="flex flex-col gap-[46px] w-[488px]">
            <p className="font-['Inter'] font-normal text-[16px] text-[#bebebe] leading-[1.4]">
              {t("exp.banner.desc")}
            </p>
            <AnimatedCTA href={link}>{t("exp.banner.cta")}</AnimatedCTA>
          </div>
        </div>

        {/* Mobile */}
        <div className="xl:hidden flex flex-col gap-[20px] pt-[44px] pb-[60px]">
          <p className="font-['Inter'] font-medium text-[12px] text-[#636363] tracking-[1.4px] uppercase leading-[1.2]">
            {t("exp.banner.subtitle")}
          </p>
          <h1 className="font-['Montserrat'] font-black text-[32px] text-[#e1e1e1] tracking-[-1.28px] leading-[1.2]">
            {t("exp.banner.title")}
          </h1>
          <p className="font-['Inter'] font-normal text-[14px] text-[#bebebe] leading-[1.4]">
            {t("exp.banner.desc")}
          </p>
          <AnimatedCTA href={link} mobile full>{t("exp.banner.cta")}</AnimatedCTA>
        </div>

      </div>
    </section>
  );
}

export default Banner;
